import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  Radio,
  Row,
  Select,
  message,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import {
  formatNumbers,
  formatNumber,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
// FIX #1: removed invalid 'data' import from react-router-dom
import { useParams } from "react-router-dom";

interface CreditBillingPaymentFormProps {
  add?: boolean;
  edit?: boolean;
}

const CreditBillingPaymentForm: React.FC<CreditBillingPaymentFormProps> = ({
  add,
  edit,
}) => {
  const { id: credit_billing_payment_id } = useParams<{ id: string }>();

  const [errorMessage,   setErrorMessage]   = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading,        setLoading]        = useState("");
  const [companies,      setCompanies]      = useState<any[]>([]);
  const [companyId,      setCompanyId]      = useState<number | null>(null);
  const [form]                              = Form.useForm();

  const itemsArray  = useWatch("itemsArray", form);
  const paymentMode = useWatch("payment_mode", form);
  const amountPaid  = useWatch("amount", form);

  // Grand total from all billing items
  const grandTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.amount) || 0);
  }, 0);

  // Auto-computed change: amount paid - grand total
  const computedChange = Math.max(0, (Number(amountPaid) || 0) - grandTotal);

  function clearFields() {
    form.resetFields();
    setCompanyId(null);
  }

  // ─── Fetch Companies ──────────────────────────────────────────────────────────
  async function fetchCompanies() {
    setCompanies([]);
    setLoading("fetch");
    const response = await getRequest(`companies/get`, {}, true);
    if (response.success) setCompanies(response.data.data);
    setLoading("");
  }

  // ─── Fetch Unpaid Credit Billings by Company ─────────────────────────────────
  async function fetchCreditBillings(company_id: number) {
    const response = await getRequest(`credit_billings/get`, { company_id: company_id, status: "active" }, true);
    if (response.success) {
      const items = response.data.data.map((credit_billing: any) => ({
        billing_no: credit_billing.statement_no,
        amount:     credit_billing["2307_calculation"],
      }));
      form.setFieldsValue({ itemsArray: items });
    } else {
      message.error("Failed to fetch credit billings.");
      form.setFieldsValue({ itemsArray: [{}] });
    }
  }

  // ─── Company Change Handler ──────────────────────────────────────────────────
  function handleCompanyChange(value: any) {
    const selectedCompany = companies.find((c: any) => Number(c.id) === Number(value));
    if (selectedCompany) {
      form.setFieldsValue({ cellphone_no: selectedCompany.cellphone_no });
      setCompanyId(selectedCompany.id ? Number(selectedCompany.id) : null);
    } else {
      setCompanyId(null);
    }

    form.setFieldsValue({ itemsArray: [{}] });
    if (value) fetchCreditBillings(value);
  }

  // ─── Submit ───────────────────────────────────────────────────────────────────
  async function submitForm(data: any) {
    const formattedData: any = {
      ...data,
      grand_total: grandTotal,
    };

    if (data.payment_mode === "cash") {
      formattedData.amount       = data.amount;
      formattedData.change       = computedChange;
      formattedData.bank_name    = "";
      formattedData.check_date   = "";
      formattedData.check_number = "";
      formattedData.reference_no = "";
    } else if (data.payment_mode === "check") {
      formattedData.amount       = grandTotal;
      formattedData.change       = "";
      formattedData.bank_name    = data.bank_name;
      formattedData.check_date   = data.check_date;
      formattedData.check_number = data.check_number;
      formattedData.reference_no = "";
    } else if (data.payment_mode === "transfer") {
      formattedData.amount       = grandTotal;
      formattedData.change       = "";
      formattedData.bank_name    = data.bank_name;
      formattedData.check_date   = "";
      formattedData.check_number = "";
      formattedData.reference_no = data.reference_no;
    }

    setLoading("submit");
    const response = await postRequest(
      add ? `credit_billing_payments/create` : `credit_billing_payments/update`,
      add ? formattedData : { ...formattedData, credit_billing_payment_id },
      true
    );

    if (response.success) {
      // FIX #5: use setSuccessMessage to trigger SuccessModal instead of antd message
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        setSuccessMessage("");
        navigateTo("/credit-billing-payment");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${add ? "create" : "update"} payment.`
      );
    }
  }

  // ─── Effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-update change field when amount paid or grand total changes
  useEffect(() => {
    if (paymentMode === "cash") {
      form.setFieldsValue({ change: computedChange.toFixed(2) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountPaid, grandTotal]);

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={add ? "Add Credit Billing Payment" : "Edit Credit Billing Payment"}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>

          <Col span={24}>
            <Form form={form} onFinish={submitForm} layout="vertical">
              <div className="form-container">
                <Row className="w-full" gutter={[10, 10]}>

                  {/* ── Customer ──────────────────────────────── */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Company Name"
                      name="company_id"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <Select
                        className="w-full"
                        placeholder="Select company"
                        showSearch
                        allowClear
                        onChange={handleCompanyChange}
                        optionFilterProp="label"
                        options={companies.map((c: any) => ({
                          label: c.name,
                          value: c.id,
                        }))}
                      />
                    </Form.Item>
                  </Col>

                  {/* ── Credit Billing List ───────────────────── */}
                  <Col xs={24} className="py-1">
                    <span className="edit-label mb-2">
                      <label className="text-red-500 me-1">*</label>
                      List of Credit Billing Statements:
                    </span>
                  </Col>

                  <Col xs={24}>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { remove }) => (
                        <>
                          <div className="overflow-auto max-h-[600px]">
                            <table className="w-full table-fixed border border-collapse min-w-[800px]">
                              <thead>
                                <tr className="bg-primary text-white font-bold border">
                                  <th className="p-2 border w-[60px]">Billing No.</th>
                                  <th className="p-2 border w-[60px]">Amount</th>
                                  <th className="p-2 border w-[40px] text-center">Action</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fields.map((field) => (
                                  <tr key={field.key} className="border bg-white">
                                    <td className="p-2 border">
                                      <Form.Item
                                        name={[field.name, "billing_no"]}
                                        rules={[{ required: true, message: "Required" }]}
                                        noStyle
                                      >
                                        <Input disabled />
                                      </Form.Item>
                                    </td>
                                    <td className="p-2 border">
                                      <Form.Item
                                        name={[field.name, "amount"]}
                                        rules={[{ required: true, message: "Required" }]}
                                        noStyle
                                      >
                                        <Input
                                          type="number"
                                          min={0}
                                          step="0.01"
                                          disabled
                                          onWheel={(e) =>
                                            (e.target as HTMLInputElement).blur()
                                          }
                                        />
                                      </Form.Item>
                                    </td>
                                    <td className="p-2 border text-center">
                                      <Button
                                        onClick={() => remove(field.name)}
                                        icon={<Trash size={17} />}
                                        danger
                                        type="text"
                                      />
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <div className="mt-2 text-end">
                            <strong>Grand Total: ₱ {formatNumbers(grandTotal)}</strong>
                          </div>
                        </>
                      )}
                    </Form.List>
                  </Col>

                  {/* ── Payment Details ───────────────────────── */}
                  <Col xs={24} className="py-1 mt-2">
                    <strong>Payment Details</strong>
                  </Col>

                  <Col xs={24} lg={6} className="me-3">
                    <Form.Item
                      label="Mode of Payment"
                      name="payment_mode"
                      initialValue="cash"
                      rules={[{ required: true, message: "This field is required." }]}
                    >
                      <Radio.Group buttonStyle="solid">
                        <Radio.Button value="cash">Cash</Radio.Button>
                        <Radio.Button value="check">Check</Radio.Button>
                        <Radio.Button value="transfer">Transfer</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>

                  {/* ── Cash Fields ──────────────────────────── */}
                  {paymentMode === "cash" && (
                    <>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Amount Paid (₱)"
                          name="amount"
                          rules={[
                            { required: true, message: "This field is required." },
                            {
                              validator: (_, value) =>
                                Number(value) >= grandTotal
                                  ? Promise.resolve()
                                  : Promise.reject(
                                      new Error("Amount paid must be ≥ grand total.")
                                    ),
                            },
                          ]}
                        >
                          <Input
                            type="number"
                            step="0.01"
                            min={grandTotal}
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item label="Change (₱)" name="change">
                          <Input
                            type="number"
                            step="0.01"
                            disabled
                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                          />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {/* ── Check Fields ─────────────────────────── */}
                  {paymentMode === "check" && (
                    <>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Bank Name"
                          name="bank_name"
                          rules={[{ required: true, message: "This field is required." }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Check Date"
                          name="check_date"
                          rules={[{ required: true, message: "This field is required." }]}
                        >
                          <DatePicker className="w-full" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Check Number"
                          name="check_number"
                          rules={[{ required: true, message: "This field is required." }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {/* ── Transfer Fields ───────────────────────── */}
                  {paymentMode === "transfer" && (
                    <>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Bank Name"
                          name="bank_name"
                          rules={[{ required: true, message: "This field is required." }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                      <Col xs={24} lg={5}>
                        <Form.Item
                          label="Reference Number"
                          name="reference_no"
                          rules={[{ required: true, message: "This field is required." }]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    </>
                  )}

                  {/* ── Actions ──────────────────────────── */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Button
                      onClick={() => {
                        navigateTo("/credit-billing-payment");
                        clearFields();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      htmlType="submit"
                      type="primary"
                      loading={loading === "submit"}
                    >
                      {add ? "Submit" : "Update"}
                    </Button>
                  </Col>

                </Row>
              </div>
            </Form>
          </Col>
        </Row>
      </Layout>

      {/* Error Modal */}
      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      {/* FIX #5: SuccessModal now triggered via setSuccessMessage in submitForm */}
      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <PersistentFooter />
    </Layout>
  );
};

export default CreditBillingPaymentForm;